// valet-keychain-qr — qr-batch-generation: issued batches list + reprint action.

import { useTranslation } from '@jq4race/i18n';
import type { QrBatchSummary } from '@jq4race/shared';
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { formatDate } from '../../../../shared/format/format-date';

export interface QrBatchListProps {
  batches: QrBatchSummary[];
  loading: boolean;
  reprintingId: string | null;
  onReprint: (batchId: string) => void;
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

function formatRange(b: QrBatchSummary): string {
  return `${b.prefix}-${pad4(b.rangeStart)} .. ${b.prefix}-${pad4(b.rangeEnd)}`;
}

export function QrBatchList({ batches, loading, reprintingId, onReprint }: QrBatchListProps) {
  const { t, i18n } = useTranslation('qr-generator');

  return (
    <div className="space-y-3">
      <h3 className="text-[14px] font-medium text-jq-charcoal">{t('batches.heading')}</h3>
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : batches.length === 0 ? (
        <p className="text-[13px] text-jq-graphite">{t('batches.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('batches.columns.prefix')}</TableHead>
              <TableHead>{t('batches.columns.range')}</TableHead>
              <TableHead align="right">{t('batches.columns.quantity')}</TableHead>
              <TableHead>{t('batches.columns.createdAt')}</TableHead>
              <TableHead align="right">{t('batches.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((batch) => (
              <TableRow key={batch.id}>
                <TableCell>{batch.prefix}</TableCell>
                <TableCell>{formatRange(batch)}</TableCell>
                <TableCell align="right">{batch.quantity}</TableCell>
                <TableCell>{formatDate(batch.createdAt, i18n.language)}</TableCell>
                <TableCell align="right">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={reprintingId === batch.id}
                    onClick={() => onReprint(batch.id)}
                  >
                    {reprintingId === batch.id ? t('batches.reprinting') : t('batches.reprint')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
